import React, { useEffect, useState, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Avatar from "react-avatar";
import {
  getContactDetails,
  createContactDetails,
  updateContactDetails,
  updateProfile,
  getProfile,
} from "api/User";

import { UserContext } from "context/user";
import InputField from "components/Input";
import Button from "components/Button";
import CheckBox from "components/CheckBox";
import termsAndCondition from "assets/images/datafarming-terms-and-condition.pdf";
import privacyPolicy from "assets/images/datafarming-collection-notice.pdf";
import "./index.scss";
import { toast } from "react-toastify";

const ProfileSetupPage = () => {
  const history = useHistory();
  const { token, user } = useContext(UserContext);
  const [isTermsAndConditionChecked, setTermsAndConditionChecked] =
    useState(false);
  const [isPrivacyPolicyChecked, setPrivacyPolicyChecked] = useState(false);
  const [emailDomain, setEmailDomain] = useState("");
  const [tempEmail, setTempEmail] = useState("");
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    userType: "",
    address1: "",
    city: "",
    country: "",
    state: "",
    suburb: "",
    zipCode: "",
    email: emailDomain === "twitter.df" ? "" : user?.email,
  });
  const [formValuesError, setFormValuesError] = useState({
    firstName: "",
    lastName: "",
    userType: "",
    email: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [formType, setFormType] = useState("");

  const fetchData = useCallback(async () => {
    if (!token) return;

    try {
      const userDetailsData = await getContactDetails(token);
      setUserDetails(userDetailsData.data);
    } catch (e) {
      setUserDetails(null);
    }
  }, [token]);

  const fetchProfile = useCallback(async () => {
    if (!token) return;

    try {
      const userProfile = await getProfile(token);
      if (userProfile.account.email) {
        const userDomain = userProfile.account.email.split("@")[1];
        setEmailDomain(userDomain);
        setTempEmail(userProfile.account.email);
      }
    } catch (e) {
      toast.error("We encountered an error.");
    }
  }, [token]);

  useEffect(() => {
    if (userDetails) {
      if (emailDomain !== "twitter.df") {
        setFormValues({
          firstName: userDetails?.first_name || "",
          lastName: userDetails?.last_name || "",
          userType: userDetails?.user_type || "",
          address1: userDetails?.address1 || "",
          country: userDetails?.country || "",
          state: userDetails?.state || "",
          city: userDetails?.city || "",
          suburb: userDetails?.suburb || "",
          zipCode: userDetails?.post_code || "",
          email: user?.email || "",
        });
        setFormType("update");
      } else {
        setFormValues({
          firstName: userDetails?.first_name || "",
          lastName: userDetails?.last_name || "",
          userType: userDetails?.user_type || "",
          address1: userDetails?.address1 || "",
          country: userDetails?.country || "",
          state: userDetails?.state || "",
          city: userDetails?.city || "",
          suburb: userDetails?.suburb || "",
          zipCode: userDetails?.post_code || "",
          email: "",
        });
        setFormType("update");
      }
    } else {
      if (emailDomain === "twitter.df") {
        setFormValues({
          firstName: userDetails?.first_name || "",
          lastName: userDetails?.last_name || "",
          userType: userDetails?.user_type || "",
          address1: userDetails?.address1 || "",
          country: userDetails?.country || "",
          state: userDetails?.state || "",
          city: userDetails?.city || "",
          suburb: userDetails?.suburb || "",
          zipCode: userDetails?.post_code || "",
          email: "",
        });
      } else {
        setFormValues({
          firstName: userDetails?.first_name || "",
          lastName: userDetails?.last_name || "",
          userType: userDetails?.user_type || "",
          address1: userDetails?.address1 || "",
          country: userDetails?.country || "",
          state: userDetails?.state || "",
          city: userDetails?.city || "",
          suburb: userDetails?.suburb || "",
          zipCode: userDetails?.post_code || "",
          email: userDetails?.email || "",
        });
      }
      setFormType("add");
    }
  }, [userDetails, user, emailDomain, tempEmail]);

  useEffect(() => {
    fetchData();
    fetchProfile();
  }, [fetchData, fetchProfile]);

  const validateField = (fieldName, value) => {
    let error;
    if (fieldName === "firstName") {
      error = value ? "" : "Enter First Name";
    } else if (fieldName === "lastName") {
      error = value ? "" : "Enter Last Name";
    } else if (fieldName === "email") {
      error = value ? "" : "Enter Email Address";
    } else if (fieldName === "userType") {
      error = value ? "" : "Select Occupation";
    }
    return error;
  };

  const isErrorListHasValue = (errorList) => {
    const isListHasError = Object.values(errorList).some(
      (error) => error !== null && error !== "" && typeof error !== "undefined"
    );
    return isListHasError;
  };

  const validateOnSubmit = (objectList) => {
    const errorList = {};
    Object.keys(objectList).forEach((key) => {
      if (key !== "name") {
        errorList[key] = validateField(key, objectList[key]);
      }
    });
    setFormValuesError(errorList);
    return errorList;
  };

  const validateInput = (e) => {
    e.preventDefault();
    const { value, name } = e.target;
    const fieldError = validateField(name, value);
    const newError = {
      ...formValuesError,
      [name]: fieldError || "",
    };
    setFormValuesError(newError);
  };

  const handleInputChange = (event) => {
    event.preventDefault();
    validateInput(event);
    const { value, name } = event.target;
    const newField = { ...formValues, [name]: value };
    setFormValues(newField);
  };

  const handleSaveProfileDetails = async (event) => {
    event.preventDefault();
    const formValuesErrorList = validateOnSubmit(formValues);
    if (isErrorListHasValue(formValuesErrorList)) {
      toast.error("Please fill in the required fields");
    } else {
      setIsSubmitting(true);
      const params = {
        data: {
          first_name: formValues?.firstName || "",
          last_name: formValues?.lastName || "",
          phone_number: "",
          email: emailDomain === "twitter.df" ? formValues?.email : user?.email,
          street: formValues?.streetAddress,
          suburb: formValues?.suburb,
          state: formValues?.state,
          country: formValues?.country,
          post_code: formValues?.zipCode,
          user_type: formValues?.userType || "others",
          address1: formValues?.address1,
          city: formValues?.city,
        },
      };
      let response = {};
      try {
        if (formType === "add") {
          response = await createContactDetails(params, token);
        } else {
          response = await updateContactDetails(params, token);
        }
        setIsSubmitting(false);
        if ([204, 200, 201].includes(response.status)) {
          const data = {
            terms_accepted_version: "v1",
          };
          updateProfile(data, token);
          toast.success(
            "Profile successfully Saved! Redirecting to Dashboard.."
          );
        }
      } catch (e) {
        toast.error("Something went wrong! Redirecting to Dashboard..");
      }
      const delay = setTimeout(() => {
        history.push("/dashboard");
        window.clearTimeout(delay);
      }, 3000);
    }
  };
  return (
    <div data-testid="profile-setup-page" className="profile-setup">
      <div className="terms-container">
        <p className="header">Profile Setup</p>
        <div data-testid="fullname" className="input-group">
          <div className="group">
            <InputField
              label="First Name"
              id="firstName"
              name="firstName"
              value={formValues?.firstName}
              onChange={(e) => handleInputChange(e)}
              error={formValuesError?.firstName}
            />
          </div>
          <div className="group">
            <InputField
              label="Last Name"
              id="lastName"
              name="lastName"
              value={formValues?.lastName}
              onChange={(e) => handleInputChange(e)}
              error={formValuesError?.lastName}
            />
          </div>
        </div>
        <div data-testid="email" className="input-group">
          <InputField
            label="Email Address"
            id="email"
            value={formValues?.email}
            error={formValuesError?.email}
            onChange={(e) => handleInputChange(e)}
          />
        </div>

        <div data-testid="photo">
          <p className="section-label">Photo</p>
          <div className="photo-details">
            <p className="details">
              Your photo appears in your avatar, farms collaborated with, etc.
              Recommended size: Square, at least 1000 pixels per side. File
              type: JPG or PNG.
            </p>
            <div className="avatar">
              <Avatar
                name={user?.email}
                size="70"
                src={user?.picture || ""}
                round
              />
            </div>
          </div>

          <div data-testid="user-type" className="field-section">
            <div className="input-group -solo">
              <div className="input-field">
                {formValuesError?.userType && (
                  <label htmlFor="userType" className="label error">
                    {formValuesError?.userType}
                  </label>
                )}
                <select
                  id="userType"
                  name="userType"
                  className="select-input"
                  onChange={(e) => handleInputChange(e)}
                  value={formValues?.userType}
                  error={formValues?.userType}
                >
                  <option value="">--</option>
                  <option value="farmer">Farmer</option>
                  <option value="agronomist">Agronomist</option>
                  <option value="researcher">Researcher</option>
                  <option value="input_supplier">Input Supplier</option>
                  <option value="education">Education</option>
                  <option value="other">Other</option>
                </select>
                <label htmlFor="userType" className="label">
                  Occupation <span style={{ color: "red" }}>*</span>
                </label>
              </div>
            </div>
          </div>

          {/* <div className="input-group">
            <InputField
              label="Where did you hear DataFarming?"
              // id="occupation"
              value={formValues?.email}
            />
          </div> */}
        </div>

        <div data-testid="terms-and-conditions" className="toggle">
          <CheckBox
            checked={isTermsAndConditionChecked}
            onChange={() =>
              setTermsAndConditionChecked((prevState) => !prevState)
            }
          />
          <p>
            I agree with the new Datafarming{" "}
            <a
              className="link"
              href={termsAndCondition}
              target="_blank"
              rel="noreferrer"
            >
              Terms and Conditions.
            </a>
          </p>
        </div>
        <div data-testid="privacy-policy" className="toggle">
          <CheckBox
            checked={isPrivacyPolicyChecked}
            onChange={() => setPrivacyPolicyChecked((prevState) => !prevState)}
          />
          <p>
            I agree with the new Datafarming{" "}
            <a
              className="link"
              href={privacyPolicy}
              target="_blank"
              rel="noreferrer"
            >
              Information Collection Notice.
            </a>
          </p>
        </div>
        <Button
          onClick={handleSaveProfileDetails}
          label="Start using DataFarming"
          type="solid"
          color="green"
          disabled={!isTermsAndConditionChecked || !isPrivacyPolicyChecked}
          loading={isSubmitting}
        />
      </div>
    </div>
  );
};
export default ProfileSetupPage;
